/**
 * Shared feedback primitives: confirmation dialog, toasts, refresh indicator,
 * segmented control and skeletons.
 *
 * None of these require a provider. `useConfirm` (./useConfirm) returns the dialog element the
 * caller renders, and toasts go through a tiny module store that the shell's
 * `<ToastViewport />` subscribes to, so pages stay testable in isolation.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { dismissToast, useToasts } from './toastStore';

// ------------------------------------------------------------------ confirm

export interface ConfirmOptions {
  readonly title: string;
  readonly detail?: ReactNode;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  /** Styles the confirm button as destructive. */
  readonly danger?: boolean;
  /** When set, the user must type a reason; it is returned instead of `true`. */
  readonly reasonLabel?: string;
  /** Server-side limit for the reason, enforced while typing so nothing is lost on submit. */
  readonly reasonMaxLength?: number;
}

export function ConfirmDialog({
  title,
  detail,
  confirmLabel = '确认',
  cancelLabel = '取消',
  danger = false,
  reasonLabel,
  reasonMaxLength,
  onClose,
}: ConfirmOptions & { readonly onClose: (value: boolean | string) => void }): JSX.Element {
  const titleId = useId();
  const reasonId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const [reason, setReason] = useState('');
  const needsReason = reasonLabel !== undefined;

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>('textarea, button');
    first?.focus();
    return () => previous?.focus();
  }, []);

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose(false);
      return;
    }
    if (event.key !== 'Tab' || panel.current === null) return;
    // Keep focus inside the dialog.
    const items = Array.from(
      panel.current.querySelectorAll<HTMLElement>('textarea, button:not([disabled])'),
    );
    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    if (firstItem === undefined || lastItem === undefined) return;
    if (event.shiftKey && document.activeElement === firstItem) {
      event.preventDefault();
      lastItem.focus();
    } else if (!event.shiftKey && document.activeElement === lastItem) {
      event.preventDefault();
      firstItem.focus();
    }
  };

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose(false);
      }}
    >
      <div
        ref={panel}
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
      >
        <h2 className="modal__title" id={titleId}>
          {title}
        </h2>
        {detail !== undefined ? <div className="modal__detail">{detail}</div> : null}
        {needsReason ? (
          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor={reasonId}>{reasonLabel}</label>
            <textarea
              id={reasonId}
              rows={3}
              maxLength={reasonMaxLength}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </div>
        ) : null}
        <div className="modal__actions">
          <button
            type="button"
            className="btn"
            onClick={() => {
              onClose(false);
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? 'btn btn--danger' : 'btn btn--primary'}
            disabled={needsReason && reason.trim() === ''}
            onClick={() => {
              onClose(needsReason ? reason.trim() : true);
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ toasts

/** Mounted once by each shell; renders the shared toast queue. */
export function ToastViewport(): JSX.Element {
  const items = useToasts();
  return (
    <div className="toast-viewport" role="status" aria-live="polite">
      {items.map((item) => (
        <div key={item.id} className={`toast toast--${item.tone}`}>
          <span>{item.message}</span>
          <button
            type="button"
            className="toast__close"
            aria-label="关闭提示"
            onClick={() => {
              dismissToast(item.id);
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ refresh indicator

export interface RefreshIndicatorProps {
  /** `dataUpdatedAt` from the query; 0 before the first success. */
  readonly updatedAt: number;
  readonly fetching: boolean;
  /** Shown as "自动刷新 · 每 N 秒" when set. */
  readonly intervalSeconds?: number;
  readonly onRefresh?: () => void;
  /** Rows that appeared since the previous refresh. */
  readonly added?: number;
}

export function RefreshIndicator({
  updatedAt,
  fetching,
  intervalSeconds,
  onRefresh,
  added = 0,
}: RefreshIndicatorProps): JSX.Element {
  const time =
    updatedAt > 0 ? new Date(updatedAt).toLocaleTimeString('zh-CN', { hour12: false }) : '尚未更新';
  return (
    <span className="refresh-indicator" aria-live="polite">
      <span className={fetching ? 'refresh-dot refresh-dot--active' : 'refresh-dot'} />
      <span className="faint">
        {fetching ? '更新中…' : `更新于 ${time}`}
        {intervalSeconds !== undefined ? ` · 每 ${String(intervalSeconds)} 秒自动刷新` : ''}
      </span>
      {added > 0 ? <span className="badge badge--info">新增 {added} 条</span> : null}
      {onRefresh !== undefined ? (
        <button type="button" className="btn btn--sm" onClick={onRefresh} disabled={fetching}>
          刷新
        </button>
      ) : null}
    </span>
  );
}

// ------------------------------------------------------------------ segmented control

export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  readonly label: string;
  readonly value: T;
  readonly options: readonly SegmentedOption<T>[];
  readonly onChange: (value: T) => void;
}

export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: SegmentedControlProps<T>): JSX.Element {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={option.value === value ? 'segmented__item is-active' : 'segmented__item'}
          aria-pressed={option.value === value}
          onClick={() => {
            onChange(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ skeletons

export type SkeletonKind = 'table' | 'cards' | 'chart';

export function Skeleton({
  kind = 'table',
  label,
}: {
  readonly kind?: SkeletonKind;
  readonly label: string;
}): JSX.Element {
  return (
    <div className={`skeleton skeleton--${kind}`} aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      {kind === 'cards' ? (
        Array.from({ length: 4 }, (_, i) => <div className="skeleton-card" key={i} />)
      ) : kind === 'chart' ? (
        <div className="skeleton-chart" />
      ) : (
        Array.from({ length: 5 }, (_, i) => (
          <div
            className="skeleton-bar"
            key={i}
            style={{ width: `${String(58 + ((i * 13) % 38))}%` }}
          />
        ))
      )}
    </div>
  );
}
